import { useEffect, useRef, useState } from 'react'
import parse from 'html-react-parser'
import { Label, Error } from '@progress/kendo-react-labels'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'

import {
  Upload,
  UploadFileStatus,
  UploadFileInfo,
  UploadOnAddEvent,
  UploadOnBeforeRemoveEvent,
  UploadOnBeforeUploadEvent,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload'
import {
  SurveyEngine_Models_BaseResponse,
  SurveyEngine_Models_FileUploadResponse,
} from '../../../api'
import { useGlobalCtx } from '../../../Context/GlobalContext'
import { useSurveyCtx } from '../../../Context/SurveyContext'
import {
  IntlProvider,
  LocalizationProvider,
  loadMessages,
} from '@progress/kendo-react-intl'

interface FileUploadProps {
  id: number
  question: string
  extension: string | undefined | null
  fileDescription: string | undefined | null
  maxSizeMB: number | undefined | null
  isError: boolean
  focus: boolean
  originalFileName: string | ''
  uploadedFileName: string | ''
  callback: (response: SurveyEngine_Models_BaseResponse) => void
}

export const FileUpload = (props: FileUploadProps) => {
  const [fileName, setFileName] = useState('')
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [fileValidationMessage, setFileValidationMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const labelId = `${props.id}_label`
  const errorId = `${props.id}_error`
  const editorId = `${props.id}_editor`
  const surveyCtx = useSurveyCtx()
  const globalCTX = useGlobalCtx()
  const uploadRef = useRef<Upload>(null)

  loadMessages(globalCTX.LangResources.fileUploadMsg, globalCTX.Language)

  useEffect(() => {
    if (props.isError && fileName === '') {
      setIsError(props.isError)
    }

    if (props.focus) {
      var uploadbtnElem =
        uploadRef.current?.actionElement?.parentElement?.querySelector(
          '.k-button'
        ) as HTMLButtonElement
      uploadbtnElem.focus()
      uploadbtnElem.setAttribute(
        'aria-labelledby',
        `${labelId} ${editorId}-select-button`
      )
    }

    if (props.uploadedFileName && props.originalFileName) {
      let response: SurveyEngine_Models_FileUploadResponse = {
        commandType: 'Remove',
        commandIndex: props.id,
        uploadedFileName: '',
        originalFileName: '',
      }

      setUploadedFileName(props.uploadedFileName)
      response = {
        commandType: 'FileUpload',
        commandIndex: props.id,
        uploadedFileName: props.uploadedFileName,
        originalFileName: props.originalFileName,
      }
      props.callback(response)
    }
  }, [props])

  const setDefaultFile = (): UploadFileInfo[] => {
    let uploadFileInfo: UploadFileInfo[] = []
    if (props.originalFileName && props.uploadedFileName) {
      uploadFileInfo = [
        {
          name: props.originalFileName,
          extension: '',
          progress: 100,
          status: UploadFileStatus.Uploaded,
          uid: '',
        },
      ]
    }
    return uploadFileInfo
  }

  const onUploadStatusChange = (event: UploadOnStatusChangeEvent) => {
    let response: SurveyEngine_Models_FileUploadResponse = {
      commandType: 'Remove',
      commandIndex: props.id,
      uploadedFileName: '',
      originalFileName: '',
    }

    var fileResponse = event.response ? event.response.response : null

    if (fileResponse && fileResponse.success) {
      setUploadedFileName(fileResponse.fileName)
      response = {
        commandType: 'FileUpload',
        commandIndex: props.id,
        uploadedFileName: fileResponse.fileName,
        originalFileName: fileName,
      }
    }
    props.callback(response)

    setFileUploadErrorAlert()
  }

  const onBeforeUpload = (event: UploadOnBeforeUploadEvent) => {
    setFileName(event.files[0].name)
  }

  const onBeforeRemove = (event: UploadOnBeforeRemoveEvent) => {
    event.files[0].name = uploadedFileName
  }

  const onFileAdd = (event: UploadOnAddEvent) => {
    setIsError(false)
    if (event.newState[0].validationErrors === undefined) {
      setFileValidationMessage('')
    } else if (
      event.newState[0].validationErrors &&
      event.newState[0].validationErrors[0] === 'invalidFileExtension'
    ) {
      setFileUploadErrorAlert()
      setFileValidationMessage(
        globalCTX.LangResources.fileUploadMsg.fileTypeError.replace(
          '{filetype}',
          `${props.extension}`
        )
      )
    } else if (
      event.newState[0].validationErrors &&
      event.newState[0].validationErrors[0] === 'invalidMaxFileSize'
    ) {
      setFileUploadErrorAlert()
      setFileValidationMessage(
        globalCTX.LangResources.fileUploadMsg.fileSizeError.replace(
          '{size}',
          `${props.maxSizeMB ? props.maxSizeMB * 1024 : 4000000}`
        )
      )
    }
    setFileName(event.newState[0].name)
  }
  const onFileRemove = (event: UploadOnRemoveEvent) => {
    setFileName('')
    setUploadedFileName('')
    setFileValidationMessage('')
    setIsError(false)
  }

  //need below function to read messages within upload control for screen reader
  function setFileUploadErrorAlert() {
    if (
      document.getElementsByClassName('k-file-validation-message')?.length > 0
    ) {
      document
        .getElementsByClassName('k-file-validation-message')[0]
        .setAttribute('role', 'alert')
    }
  }

  return (
    <>
      <Label
        className="py-10 text-xl font-semibold theme-file-upload-text"
        data-testid={labelId}
        id={labelId}
        editorId={editorId}
      >
        {parse(props.question.replace(/""/g, '"'))}
      </Label>
      <LocalizationProvider language={globalCTX.Language}>
        <IntlProvider locale={globalCTX.Language.substring(0, 2)}>
          <Upload
            className="my-2 p-4 theme-file-upload"
            id={editorId}
            tabIndex={0}
            ref={uploadRef}
            autoUpload={true}
            batch={false}
            multiple={false}
            defaultFiles={setDefaultFile()}
            showFileList={true}
            restrictions={{
              allowedExtensions: props.extension
                ? props.extension.split(',')
                : ['*'],
              maxFileSize: props.maxSizeMB ? props.maxSizeMB * 1024 : 4000000,
            }}
            withCredentials={false}
            onAdd={onFileAdd}
            onRemove={onFileRemove}
            saveUrl={`${window.location.origin}/${
              window.location.pathname
            }/v1/survey/uploadFile/${surveyCtx.StartSurvey.surveyInstanceId!}`}
            removeUrl={`${window.location.origin}/${
              window.location.pathname
            }/v1/survey/deleteFile/${surveyCtx.StartSurvey.surveyInstanceId!}`}
            saveHeaders={{
              Authorization: `Bearer ${surveyCtx.StartSurvey.authToken}`,
            }}
            removeHeaders={{
              Authorization: `Bearer ${surveyCtx.StartSurvey.authToken}`,
            }}
            saveMethod="POST"
            onStatusChange={onUploadStatusChange}
            onBeforeUpload={onBeforeUpload}
            onBeforeRemove={onBeforeRemove}
            saveField="file"
            removeField="fileName"
            ariaDescribedBy={errorId}
          />
        </IntlProvider>
      </LocalizationProvider>
      {isError && (
        <Error
          className="theme-validator-error-text font-semibold"
          direction="start"
          id={errorId}
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            className="theme-icon-spacer"
          />{' '}
          {globalCTX.LangResources.fileUploadMsg.noReponseError}
        </Error>
      )}
      {fileValidationMessage !== '' && (
        <Error
          className="theme-validator-error-text font-semibold"
          direction="start"
          id={errorId}
        >
          <FontAwesomeIcon
            icon={faCircleExclamation}
            className="theme-icon-spacer"
          />{' '}
          {fileValidationMessage}
        </Error>
      )}
    </>
  )
}
